"use client";

import * as React from "react";
import { Check, ArrowRight, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = ["Initial 90-minute clinical assessment", "Personalized care navigation roadmap", "Resource coordination & expert referrals", "Comprehensive follow-up documentation", "Advocacy toolkits for family members", "30 days of priority email support"];
export default function PurchaseOffer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="31077" id="core-service-comprehensive-consultation" className="py-24 bg-background overflow-hidden relative text-foreground">
  <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
    <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-x-1/2" />
    <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
  </div>

  <div ref={ref} className={`container mx-auto px-6 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div className="space-y-8">
        <div className="space-y-4">
          <div className="inline-flex items-center rounded-full border border-primary/20 bg-accent/50 px-3 py-1 text-sm font-medium text-accent-foreground">
            <Star className="mr-1 h-3.5 w-3.5 fill-accent-foreground" />
            Professional Guidance
          </div>

          <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground">
            Full Assessment Consultation
          </h2>

          <p className="text-lg text-muted-foreground font-sans leading-relaxed max-w-xl">
            This is my most comprehensive one on one session for patients and
            families who want a deeper understanding of the diagnosis,
            treatment options, and next steps. I review the available records,
            clarify complex results, and help you build a more informed path
            forward.
          </p>
        </div>

        <ul className="space-y-4">
          <li className="flex items-start gap-3 group">
            <div className="mt-1 flex-shrink-0 rounded-full bg-primary/10 p-1 group-hover:bg-primary/20 transition-colors">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <span className="text-foreground/90 font-sans font-medium">
              90 minute one on one consultation
            </span>
          </li>

          <li className="flex items-start gap-3 group">
            <div className="mt-1 flex-shrink-0 rounded-full bg-primary/10 p-1 group-hover:bg-primary/20 transition-colors">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <span className="text-foreground/90 font-sans font-medium">
              Detailed review of PSA, biopsy, imaging, and pathology
            </span>
          </li>

          <li className="flex items-start gap-3 group">
            <div className="mt-1 flex-shrink-0 rounded-full bg-primary/10 p-1 group-hover:bg-primary/20 transition-colors">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <span className="text-foreground/90 font-sans font-medium">
              Clarification of staging, risk, and key decision points
            </span>
          </li>

          <li className="flex items-start gap-3 group">
            <div className="mt-1 flex-shrink-0 rounded-full bg-primary/10 p-1 group-hover:bg-primary/20 transition-colors">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <span className="text-foreground/90 font-sans font-medium">
              Discussion of treatment options and next steps
            </span>
          </li>

          <li className="flex items-start gap-3 group">
            <div className="mt-1 flex-shrink-0 rounded-full bg-primary/10 p-1 group-hover:bg-primary/20 transition-colors">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <span className="text-foreground/90 font-sans font-medium">
              Written summary with personalized guidance
            </span>
          </li>

          <li className="flex items-start gap-3 group">
            <div className="mt-1 flex-shrink-0 rounded-full bg-primary/10 p-1 group-hover:bg-primary/20 transition-colors">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <span className="text-foreground/90 font-sans font-medium">
              Time spent answering your questions
            </span>
          </li>
        </ul>
      </div>

      <div className="relative lg:pl-8">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 h-[120%] w-[120%] rounded-full bg-gradient-to-tr from-primary/10 to-accent/20 blur-3xl opacity-60" />

        <Card className="relative border-border shadow-2xl bg-card/95 backdrop-blur-sm overflow-hidden ring-1 ring-primary/5 rounded-3xl">
          <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-bl-xl shadow-sm">
            MOST REQUESTED
          </div>

          <CardHeader className="pb-8 text-center border-b border-border/50 bg-muted/30">
            <CardTitle className="text-2xl font-serif font-bold">
              Full Assessment
            </CardTitle>
            <CardDescription className="text-base font-sans mt-2">
              A 90 minute deep dive to review your records, clarify your
              options, and map out your next steps.
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-10 pb-8 text-center">
            <div className="flex items-baseline justify-center gap-1 mb-2">
              <span className="text-6xl font-serif font-bold tracking-tighter text-foreground">$210</span>
              <span className="text-xl text-muted-foreground font-sans font-medium">
                / session
              </span>
            </div>

            <p className="text-sm text-muted-foreground font-sans px-4 max-w-xs mx-auto">
              Includes detailed record review, personalized guidance, and a
              written summary with recommended next steps.
            </p>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pb-8 px-8">
            <Button asChild size="lg" className="w-full text-base font-sans font-semibold h-14 shadow-lg shadow-primary/10 hover:shadow-primary/20 transition-all duration-300 rounded-xl">
              <Link to="/PatientBookingPage">
                Book Your Session <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>

            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground font-sans">
              <Check className="h-3 w-3 text-secondary" />
              <span>
                Best for patients making a major decision or comparing multiple
                treatment paths
              </span>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  </div>
  </section>;
}